import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function AddPlayerDialog({ open, onOpenChange, onAdded }) {
  const [league, setLeague] = useState("NBA");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ncaaTeam, setNcaaTeam] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery(""); setResults([]); setNcaaTeam(""); setLeague("NBA");
    }
  }, [open]);

  useEffect(() => {
    if (league !== "NBA" || query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.nbaSearch(query.trim());
        setResults(r.data || []);
      } catch (e) {
        toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, league]);

  const addNba = async (p) => {
    try {
      await api.addPlayer({
        league: "NBA",
        name: p.full_name,
        team: p.team || "Unknown",
        external_id: p.id,
        position: p.position || null,
        jersey_number: p.jersey_number ? String(p.jersey_number) : null,
      });
      toast.success(`${p.full_name} added`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const addManual = async () => {
    if (!query.trim() || !ncaaTeam.trim()) {
      toast.error("Enter name and team");
      return;
    }
    try {
      await api.addPlayer({ league, name: query.trim(), team: ncaaTeam.trim() });
      toast.success(`${query.trim()} added`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1c1c1c] border-white/10 text-white max-w-2xl rounded-none"
        data-testid="add-player-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl tracking-tight uppercase">Add Player</DialogTitle>
          <DialogDescription className="text-[#a0a0a0]">
            NBA lookup is live via balldontlie. NCAA-M / NCAA-W / PRO are added manually — PRO &amp; NCAA-W are profile-only (no auto-stats yet).
          </DialogDescription>
        </DialogHeader>

        {/* league toggle */}
        <div className="flex gap-0 border border-white/10 w-fit flex-wrap">
          {[
            { k: "NBA",   l: "NBA" },
            { k: "NCAA",  l: "NCAA M" },
            { k: "WNCAA", l: "NCAA W" },
            { k: "WNBA",  l: "WNBA" },
            { k: "PRO",   l: "PRO" },
            { k: "HS",    l: "HS" },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setLeague(k)}
              data-testid={`add-league-${k.toLowerCase()}`}
              className={`px-5 py-2 text-xs uppercase tracking-[0.25em] font-bold ${
                league === k ? "bg-[var(--org-accent)] text-[#121212]" : "text-[#a0a0a0] hover:text-white"
              }`}
              style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0a0]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={league === "NBA" ? "Search NBA player name (e.g. Kevin Huerter)" : "Player name"}
            className="pl-10 bg-[#101010] border-white/10 rounded-none h-12 text-white focus-visible:ring-[var(--org-accent)] focus-visible:ring-1"
            data-testid="add-player-search-input"
          />
        </div>

        {league !== "NBA" && (
          <Input
            value={ncaaTeam}
            onChange={(e) => setNcaaTeam(e.target.value)}
            placeholder={
              league === "NCAA"  ? "School (e.g. Kentucky)"
            : league === "WNCAA" ? "School (e.g. UConn)"
            : league === "WNBA"  ? "Team (e.g. Indiana Fever)"
            : league === "HS"    ? "High school (e.g. Roselle Catholic)"
            :                      "Team & league (e.g. Maroussi (Greece))"
            }
            className="bg-[#101010] border-white/10 rounded-none h-12 text-white focus-visible:ring-[var(--org-accent)] focus-visible:ring-1"
            data-testid="add-player-manual-team"
          />
        )}

        {league === "NBA" ? (
          <div className="max-h-80 overflow-y-auto border border-white/10">
            {loading && (
              <div className="flex items-center justify-center py-8 text-[#a0a0a0] gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> searching…
              </div>
            )}
            {!loading && query.length >= 2 && results.length === 0 && (
              <div className="text-center py-8 text-[#757575] text-sm">No matches</div>
            )}
            {results.map((p, idx) => (
              <div
                key={p.id}
                className="flex items-center justify-between px-4 py-3 border-b border-white/5 hover:bg-[#262626]"
                style={{ transition: "background-color 0.15s ease" }}
                data-testid={`search-result-${idx}`}
              >
                <div>
                  <div className="font-heading text-lg text-white">{p.full_name.toUpperCase()}</div>
                  <div className="text-xs uppercase tracking-widest text-[#a0a0a0]">
                    {p.team || "—"} · {p.position || "—"} {p.jersey_number ? `· #${p.jersey_number}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => addNba(p)}
                  className="flex items-center gap-1 bg-[var(--org-accent)] text-[#121212] px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold hover:bg-[var(--org-accent-hover)]"
                  style={{ transition: "background-color 0.2s ease" }}
                  data-testid={`add-btn-${idx}`}
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            ))}
          </div>
        ) : (
          <Button
            onClick={addManual}
            className="bg-[var(--org-accent)] text-[#121212] hover:bg-[var(--org-accent-hover)] rounded-none h-12 uppercase tracking-[0.25em] font-bold"
            data-testid="add-manual-submit"
          >
            <Plus className="w-4 h-4 mr-2" /> Add {league === "NCAA" ? "NCAA M" : league === "WNCAA" ? "NCAA W" : league === "WNBA" ? "WNBA" : league === "HS" ? "HS" : "PRO"} Player
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
